import React from 'react'
import { IFeatures, IProduct } from '../types'
import ProductFeature from './ProductFeatures'
import Dialog, { DialogProps } from '../../Dialog'
import ProductDescription from './ProductDescription'
import { FormProps } from '../../Form'

//MUI
import { useTheme, alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { FieldType } from '../../Form/utils/fieldTypeEnum'
import CallToAction from '../../CallToAction'
import ParrotfishIcon from '../../ParrotfishIcon'

const ProductCard = ({
  attentionText,
  alternateOffers,
  icon,
  productHeadline,
  titleIsBlue,
  description,
  secondaryHeadline,
  productQuantity,
  productQuantityDetails,
  price,
  priceDetails,
  popupFormReference,
  features,
  featureTitle,
  preFilledMessage,
  linkToLinevisitation,
}: IProduct) => {
  const { palette, breakpoints, shape } = useTheme()

  const styles = {
    root: {
      display: `grid`,
      gridAutoRows: `max-content`,
      borderRight: `solid 1px ${palette.grey[300]}`,
      backgroundColor:
        attentionText || alternateOffers === 'Mest populær' ? alpha(palette.info.contrastText, 0.5) : null,

      '&:last-child': {
        borderRight: `none`,
      },

      [breakpoints.down('sm')]: {
        borderRight: `none`,
      },
    },
    productCard: {
      overflow: `hidden`,
      height: `100%`,
      [breakpoints.down('md')]: {
        height: `unset`,
      },
    },
    productCardContent: {
      height: '100%',
      padding: '32px',
      '& > .MuiButton-root': {
        margin: `32px 0 0 0`,
      },
    },

    recommeded: {
      padding: '4px 0',
      backgroundColor: alternateOffers === 'Mest populær' || attentionText ? palette.primary.main : palette.info.light,
      color: palette.common.white,
      textAlign: 'center',
      borderTopRightRadius: shape.borderRadius,
      borderTopLeftRadius: shape.borderRadius,
      width: '100%',
      marginTop: '-27px',
      height: '27px',
      '& span': {
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.1rem',
        lineHeight: 1.3,
      },
    },
    productCardContentTitle: {
      color: titleIsBlue ? palette.primary.main : null,
      marginBottom: '12px',
    },
    productPrice: {
      '& span:first-child': {
        typography: 'h5',
        fontSize: '40px',
        lineHeight: 1.2,
      },
    },
    productPriceDetails: {
      typography: 'h5',
      fontSize: '20px',
      marginTop: '10px',
      height: '24px',
    },
    productCardContentFeatures: {
      borderTop: `solid 1px ${palette.grey[300]}`,
      paddingTop: '32px',
      marginTop: '32px',
    },
    productCardContentFeaturesTitle: {
      marginBottom: '16px',
    },
    productCardIcon: {
      fontSize: '48px',
      marginBottom: '16px',
    },
    buttons: {
      display: 'flex',
      gap: '10px',
      pt: '32px',
      flexDirection: 'column',
      '& button': {
        height: 'auto',
        width: 'fit-content',
        padding: '8px 16px',
        textAlign: 'center',
        wordWrap: 'break-word',
      },
    },
  }
  let modifiedDialogForm
  const formMessage = preFilledMessage
    ? preFilledMessage
    : `Jeg ønsker at høre mere om: ${productHeadline}, ${productQuantity || ''} ${productQuantityDetails || ''}`

  if (popupFormReference) {
    const formReference = popupFormReference.content as FormProps
    const fields = formReference?.fields
    if (fields) {
      fields.forEach(field => {
        if (field.fieldType === FieldType.MESSAGE) {
          field.value = formMessage
        }
      })
    }

    modifiedDialogForm = { ...popupFormReference, content: formReference }
  }

  const handleClick = () => {
    const element = document.getElementById('linevisitation')
    if (!element) return
    const yPosition = element.getBoundingClientRect().top + window.scrollY
    const screenCenter = window.innerHeight / 2

    window.scrollTo({
      top: yPosition - screenCenter,
      behavior: 'smooth',
    })
  }

  const linevisitationButtonText = 'Tjek jeres adresse'
  return (
    <Grid component="td" sx={styles.root} className="printet-card" item>
      {attentionText && (
        <Box sx={styles.recommeded}>
          <Typography component="span">{attentionText}</Typography>
        </Box>
      )}
      {alternateOffers && (
        <Box sx={styles.recommeded}>
          <Typography component="span">{alternateOffers}</Typography>
        </Box>
      )}
      <Box sx={styles.productCard}>
        <Box sx={styles.productCardContent} p={5}>
          {icon && <ParrotfishIcon {...icon} sx={styles.productCardIcon} />}
          {productHeadline && (
            <Typography sx={styles.productCardContentTitle} variant="h6" component="p">
              {productHeadline}
            </Typography>
          )}
          {description && <ProductDescription text={description.description} />}
          {secondaryHeadline && (
            <Typography sx={{}} variant="h5" component="p">
              {secondaryHeadline}
            </Typography>
          )}
          <Typography sx={styles.productPrice}>
            <span>{productQuantity}</span>
            {productQuantityDetails && (
              <Typography component="span" variant="h5">
                &nbsp;{productQuantityDetails}
              </Typography>
            )}
          </Typography>
          {price && (
            <Typography sx={styles.productPriceDetails}>
              {productHeadline === 'Sikker Fiber Plus' || productHeadline === '5G Internet' ? `fra ${price}` : price}{' '}
              {priceDetails && priceDetails}
            </Typography>
          )}
          <Box sx={styles.buttons}>
            {popupFormReference && modifiedDialogForm && <Dialog {...(modifiedDialogForm as DialogProps)} />}
            {linkToLinevisitation && (
              <CallToAction
                trackingId="link-to-linevisitation"
                color="info"
                buttonText={linevisitationButtonText}
                onClick={handleClick}
              />
            )}
          </Box>

          {features && features.length > 0 && (
            <Box sx={styles.productCardContentFeatures}>
              {featureTitle && (
                <Typography sx={styles.productCardContentFeaturesTitle} variant="subtitle1">
                  {featureTitle.charAt(0).toUpperCase() + featureTitle.slice(1)}
                </Typography>
              )}
              <ProductFeature features={features as IFeatures[]} />
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  )
}

export default ProductCard
